import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { emit } from './dom.utils.js';
import { resolveMessage, type ValidationMessages } from './validation-messages.js';

export interface ValidationConstraints {
  required?: boolean;
  minlength?: number;
  maxlength?: number;
  min?: number | string;
  max?: number | string;
  step?: number;
  pattern?: string;
  type?: string;
}

export interface FormValidationHost extends ReactiveControllerHost {
  readonly _internals: ElementInternals;
  invalid: boolean;
}

export class FormValidationController implements ReactiveController {
  private _host: FormValidationHost;
  private _anchor: HTMLElement | null = null;
  private _dirty   = false;
  private _touched = false;
  private _messages?: Partial<ValidationMessages>;

  constructor(host: FormValidationHost, messages?: Partial<ValidationMessages>) {
    this._host     = host;
    this._messages = messages;
    host.addController(this);
  }

  hostConnected()  { /* nothing */ }
  hostDisconnected() { /* nothing */ }

  hostUpdated() {
    this._syncAriaAttrs();
  }

  setAnchor(el: HTMLElement | null) {
    this._anchor = el;
  }

  get dirty():   boolean { return this._dirty; }
  get touched(): boolean { return this._touched; }
  get pristine(): boolean { return !this._dirty; }

  handleChange(value: string, constraints: ValidationConstraints) {
    this._dirty = true;
    this._validate(value, constraints);
    this._host.requestUpdate();
  }

  setTouched(touched: boolean) {
    this._touched = touched;
    if (touched) {
      this._fireInvalidIfNeeded();
    }
    this._host.requestUpdate();
  }

  revalidate(value: string, constraints: ValidationConstraints) {
    this._validate(value, constraints);
    this._host.requestUpdate();
  }

  syncFormValue(value: string | null) {
    this._host._internals.setFormValue(value);
  }

  reset() {
    this._dirty   = false;
    this._touched = false;
    this._host._internals.setValidity({});
    this._host.requestUpdate();
  }

  // ── Private ────────────────────────────────────────────────────────

  private _validate(value: string, c: ValidationConstraints) {
    const flags: ValidityStateFlags = {};
    let message = '';

    const v = value ?? '';

    if (c.required && !v.trim()) {
      flags.valueMissing = true;
    }

    if (!flags.valueMissing && v) {
      if (c.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        flags.typeMismatch = true;
      } else if (c.type === 'url') {
        try { new URL(v); } catch { flags.typeMismatch = true; }
      }
    }

    if (!flags.typeMismatch && c.pattern && v) {
      const re = new RegExp(`^(?:${c.pattern})$`);
      if (!re.test(v)) flags.patternMismatch = true;
    }

    if (c.minlength !== undefined && v.length > 0 && v.length < c.minlength) {
      flags.tooShort = true;
    }

    if (c.maxlength !== undefined && v.length > c.maxlength) {
      flags.tooLong = true;
    }

    const hasFlags = Object.values(flags).some(Boolean);

    if (hasFlags) {
      message = resolveMessage(flags, this._messages);
      this._host._internals.setValidity(flags, message, this._anchor ?? undefined);
    } else {
      this._host._internals.setValidity({});
    }

    this._host.invalid = hasFlags;
  }

  private _fireInvalidIfNeeded() {
    if (!this._host._internals.validity.valid) {
      emit(this._host as unknown as HTMLElement, 'uwc-invalid', {
        validity:          this._host._internals.validity,
        validationMessage: this._host._internals.validationMessage,
      });
    }
  }

  private _syncAriaAttrs() {
    if (!this._anchor) return;
    const invalid = !this._host._internals.validity.valid && this._touched;
    if (invalid) {
      this._anchor.setAttribute('aria-invalid', 'true');
    } else {
      this._anchor.removeAttribute('aria-invalid');
    }
  }
}
